import { Formik, Form } from 'formik'
import {
  propEq,
  isEmpty,
  omit,
  map,
  prop,
  equals,
  filter,
  complement,
  concat,
  not,
  any,
  path
} from 'ramda'
import { Userpilot } from 'userpilot'
import { useState, useRef, useEffect } from 'react'
import { useTour } from '@reactour/tour'
import { Helmet } from 'react-helmet-async'
import moment from 'moment'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Prompt } from 'react-router'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Inline, Hidden, useToast } from '@machobear/design-system'
import useMediaQuery from '@/hooks/use-media-query'
import { isNullOrEmpty } from '@/utils'
import toBoolean from '@/utils/to-boolean'
import { useToaster } from '@/models/toaster/hooks'
import useCurrentSession from '@/hooks/use-current-session'
import toInteger from '@/utils/to-integer'
import { useBoostListingWithExistingCredits } from '@/hooks/use-sponsored-listing'
import { useGetUsage } from '@/hooks/use-block-and-pay'
import { show as showVerifyPhoneModal } from '@/components/verify-phone-modal'
import useSearchParams from '@/hooks/use-search-params'

import AreaTitle from '@/components/area-title'
import { Button } from '@/components/button'
import ContentWrapper from '@/components/content-wrapper'
import { Error } from '@/components/form'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import Panel from '@/components/panel'
import { toast } from '@/components/toast-notifications'
import BuildingInfo from '@/features/Building/building-info/small'
import LandlordManagement from '@/features/Listing/landlord-management'
import SponsoredListing from '@/features/Listing/sponsored-listing'
import ListingButtonBar from '@/features/Listing/button-bar'
import { breakpoints } from '@/styles'
import queryClient from '@/components/query-client'
import config from '@/config'
import { pushToDatalayer } from '@/utils/push-datalayer'
import Terms from './terms'
import ParkingStorage from './parking-storage'
import ImageUpload from './image-upload'
import Features from './features'
import Details from './details'
import Description from './description'
import VideoUpload from './VideoUpload'

const { WEB_URL } = config

const slCreditsTrackingIds = {
  'seven-days-sponsored-listing-package': 'goal_purchase_credits_sl_7d',
  'fourteen-days-sponsored-listing-package': 'goal_purchase_credits_sl_14d',
  'thirty-days-sponsored-listing-package': 'goal_purchase_credits_sl_28d'
}

const isDraft = propEq('state_machine', 'draft')

const StyledButton = styled(Button)`
  margin-left: auto;
`

const isPrimary = propEq('primary', true)
const findPrimaryLandlord = filter(isPrimary)
const findSecondaryLandlords = filter(complement(isPrimary))

const Detail = ({ data, listingData, history }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const toaster = useToaster()
  const showToast = useToast()
  const updateUnit = dispatch.listing.updateUnit
  const sortImages = dispatch.listing.sortImages
  const uploadToast = useRef()
  const session = useCurrentSession()
  const [uploading, setUploading] = useState(false)
  const { listingId } = useParams()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const searchParams = useSearchParams()
  const couponCode = searchParams?.couponCode
  const userId = useSelector(state => state.profile?.id)
  const [primaryLandlord, setPrimaryLandlord] = useState([])
  const [secondaryLandlords, setSecondaryLandlords] = useState([])
  const [isBoostYourListing, setIsBoostYourListing] = useState(false)
  const [boostYourListingData, setBoostYourListingData] = useState({})

  const getDuration = value => {
    switch (value) {
      case 'seven-days-sponsored-listing-package':
        return '7'
      case 'fourteen-days-sponsored-listing-package':
        return '14'
      case 'thirty-days-sponsored-listing-package':
        return '28'
      default:
        return '7'
    }
  }

  const { mutate: boostListingWithExistingCredits } = useBoostListingWithExistingCredits()
  const { data: usage } = useGetUsage('sponsored_listing_days')

  const hideListingPublished = toBoolean(
    useSelector(path(['session', 'user_flags', 'skip_listing_published_confirmation', 'key_value']))
  )

  const storedData = JSON.parse(localStorage.getItem('sponsored-tour'))

  const purchased = usage?.blocks?.sponsored_listing_days?.value
  const used = usage?.blocks?.sponsored_listing_days?.usage?.current_value

  const count = purchased - used
  const boostWithCredits = count >= parseInt(getDuration(boostYourListingData?.duration))

  const getSuccessToasterText = body => {
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const startTime = moment(body?.planned_start_date).tz(localTimezone).format('hh:mm A')
    const startDate = moment(body?.planned_start_date).tz(localTimezone).format('YYYY-MM-DD')

    const isToday = moment(body?.planned_start_date).tz(localTimezone).isSame(new Date(), 'day')
    const isTomorrow = moment(body?.planned_start_date)
      .tz(localTimezone)
      .subtract(1, 'day')
      .isSame(new Date(), 'day')

    if (isToday)
      return t(
        'l.listing.sponsored_listings.success_today.toaster',
        `Success! This listing will be sponsored today at {{ startTime }}`,
        { startTime }
      )
    else if (isTomorrow) {
      return t(
        'l.listing.sponsored_listings.success_tomorrow.toaster',
        `Success! This listing will be sponsored tomorrow at {{ startTime }}`,
        { startTime }
      )
    } else {
      return t(
        'l.listing.sponsored_listings.success_future.toaster',
        `Success! This listing will be sponsored on {{ startDate }} at {{ startTime }}`,
        { startTime, startDate }
      )
    }
  }

  useEffect(() => {
    dispatch.companyManagement.getCompanyListings(true)
  }, [dispatch.companyManagement])

  useEffect(() => {
    if (!listingData) return
    setPrimaryLandlord(findPrimaryLandlord(listingData?.listing_landlords))
    setSecondaryLandlords(findSecondaryLandlords(listingData?.listing_landlords))
  }, [listingData])

  const videoSuccessMessage = () => {
    const { full_street_name, city, state } = data.building
    const videoAddress = `${full_street_name}, ${city} ${state}`
    return (
      <Trans i18nKey="l.listing.edit.video_added_to.text">
        The video has been added to <strong>{{ videoAddress }}</strong>.
      </Trans>
    )
  }

  const videoFailMessage = () => {
    const { full_street_name, city, state } = data.building
    const videoAddress = `${full_street_name}, ${city} ${state}`
    return (
      <Trans i18nKey="l.listing.edit.video_upload_fail.text">
        The video upload for <strong>{{ videoAddress }}</strong> failed. Please ensure the uploaded
        file is a valid video file.
      </Trans>
    )
  }

  const saveImageSort = async images => {
    const { response } = await sortImages(images)
    return response
  }

  // Handle tour

  const { setIsOpen, isOpen, setCurrentStep } = useTour()

  const toggleTour = step => {
    setCurrentStep(step)
    if (step === 8) {
      pushToDatalayer({ tourId: 'sllp-react' })
    }
    setIsOpen(!isOpen)
  }

  // Adds scroll listener to trigger boost listing tour section
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('sponsored-tour'))
    const isCreate = window.location.href?.includes('create') && isDraft(data)

    const scrollTour = () => {
      clearTimeout(isScrolling)

      isScrolling = setTimeout(function () {
        if (window.scrollY > 4000) {
          toggleTour(8)
          window.removeEventListener('scroll', scrollTour)
        }
      }, 200)
    }
    let isScrolling
    if (
      storedData &&
      storedData?.publishListing === false &&
      storedData?.boostListing === true &&
      isCreate
    ) {
      window.addEventListener('scroll', scrollTour)
    }

    return () => {
      window.removeEventListener('scroll', scrollTour)
    }
  }, [])

  const save = async values => {
    const oldImageOrder = map(prop('id'), data.images)
    const newImageOrder = map(prop('id'), values.images)

    if (values.images && values?.images?.length > 0 && !equals(oldImageOrder, newImageOrder)) {
      const sortResponse = await saveImageSort(values.images)
      if (!sortResponse.ok) return sortResponse
    }
    values?.noLoading && dispatch.loading.setLoading()
    const { response } = await updateUnit(values)

    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ['listing-info', listingId] })
    }
    return response
  }

  const saveAndExit = async values => {
    const response = await save(values)
    if (response.ok) {
      history.push('/listings')
    }
  }

  const handlePreview = async values => {
    const formValues = { removeQuery: false, ...values }
    const response = await save(formValues)

    if (response.ok) {
      const previewUrl = `${WEB_URL}/rental-listings/${listingId}`
      return window.open(previewUrl, '_blank')
    }
  }

  const handleVerifyPhoneModal = (values, setSubmitting, publishListing) =>
    showVerifyPhoneModal({
      history,
      customFunc: () => saveAndPublish(values, setSubmitting, publishListing)
    })

  const saveAndPublish = async (values, setSubmitting, publishListing = true) => {
    if (couponCode) {
      pushToDatalayer({
        event: 'application-publish-listing-submit',
        couponCode: couponCode
      })
    } else {
      pushToDatalayer({
        event: 'application-publish-listing-submit'
      })
    }

    const formValues = { noLoading: true, removeQuery: false, ...values }
    const response = await save(formValues)

    if (response.ok) {
      const landlordData = concat(primaryLandlord, secondaryLandlords)
      const removedSelf = not(any(propEq('id', userId), landlordData))
      const formattedLandlordData = {
        listing_id: data.listing_id,
        primary_user_id: toInteger(findPrimaryLandlord(landlordData)[0].id),
        listing_landlords: landlordData.map(landlord => toInteger(landlord.id))
      }
      await dispatch.listing.updateLandlords(formattedLandlordData, { noLoading: true })

      if (removedSelf) {
        dispatch.loading.stopLoading()
        history.push('/listings')
      } else {
        setSubmitting(false)
        publishListing ? handlePublish() : handleUpdateListingAfterSave()
      }
      showToast({
        message: t('l.listing.edit.listing_edited_toast_text', 'Your listing has been saved!'),
        tone: 'positive'
      })
    } else {
      setSubmitting(false)
      dispatch.loading.stopLoading()
    }
  }

  const handleSponsorParams = () => {
    if (isBoostYourListing && !boostWithCredits) {
      history.replace(
        `/listing/${listingId}?createListing=1&sl=1&duration=${boostYourListingData.duration}`
      )
    } else if (isBoostYourListing && boostWithCredits) {
      history.replace(`/listing/${listingId}?createListing=1&nextSteps=1`)
    }
  }

  const handleShowPublishModal = () => {
    if (!hideListingPublished) {
      handleSponsorParams()
      if (storedData && storedData?.listingDashboard === false) {
        if (isMobile) {
          toggleTour(7)
        } else {
          toggleTour(4)
        }
      }
    } else {
      handleSponsorParams()
    }
  }

  const handleSuccessfulSponsor = result => {
    queryClient.invalidateQueries({ queryKey: ['sponsored-listings'] })
    pushToDatalayer({
      event: 'app_sponsor_create_cr_purchased'
    })

    const productTrackingId = slCreditsTrackingIds[boostYourListingData?.duration]

    const trackingId = !!isDraft(data) ? `${productTrackingId}_create` : productTrackingId
    Userpilot.track(trackingId, { price: boostYourListingData?.product.value })
    localStorage.removeItem('promoType')
    showToast({ message: getSuccessToasterText(result?.body), tone: 'positive' })
  }

  const handleUpdateListingAfterSave = async () => {
    if (isBoostYourListing && boostWithCredits) {
      boostListingWithExistingCredits({
        body: {
          listing_id: parseInt(listingId),
          plan_name: boostYourListingData.product?.plan_name,
          product_name: 'sponsored-listing-product',
          immediate_start: true
        },
        listingId: listingId,
        onSuccess: data => handleSuccessfulSponsor(data)
      })
      pushToDatalayer({
        event: 'create-application-sponsor-listing-credit-checkout-submit-purchase-success',
        purchaseId: boostYourListingData.product?.plan_name
      })
    }

    const current = path(['tasks', 'publish_listing', 'steps', 'create', 'state'])(toaster)
    if (current && current !== 'complete') {
      const state = {
        step: { state: 'complete' },
        next: [
          { key: 'verify', step: { state: 'incomplete' } },
          { key: 'share', step: { state: 'incomplete' } }
        ]
      }
      await toaster.updateState('publish_listing.create', state)
      toaster.fetch()
    }

    dispatch.loading.stopLoading()
    dispatch.listing.remove([listingId])
    if (isBoostYourListing && !boostWithCredits) {
      history.push(
        `/listing/${listingId}?published=1&createListing=1&sl=1&duration=${boostYourListingData.duration}`
      )
    } else if (isBoostYourListing && boostWithCredits) {
      history.push(`/listing/${listingId}?published=1&createListing=1&nextSteps=1`)
    } else {
      history.push(`/listing/${listingId}`)
    }

    return handleShowPublishModal()
  }
  const handlePublish = async () => {
    const { response } = await dispatch.listing.update(
      {
        listing_id: listingId,
        state_machine: 'published'
      },
      { noLoading: true }
    )
    if (response.ok) {
      handleUpdateListingAfterSave()
    } else {
      dispatch.loading.stopLoading()
    }
  }

  const getButtonText = () =>
    isDraft(data)
      ? t('l.listing.edit.next.button', 'Publish')
      : t('l.listing.edit.update.button', 'Update')

  const validateMin = (values, min, max) => {
    if (values[max] && values[min] && +values[min] > +values[max]) {
      return {
        [min]: t('l.listing.edit.min_max_range.error', "Value can't be greater than the max value")
      }
    }
    return {}
  }

  const validateMax = (values, min, max) => {
    if (values[min] && values[max] && +values[max] < +values[min]) {
      return {
        [max]: t('l.listing.edit.max_min_range.error', "Value can't be less than the min value")
      }
    }
    return {}
  }

  const validateMinMax = (values, min, max) => {
    return {
      ...validateMin(values, min, max),
      ...validateMax(values, min, max)
    }
  }

  const formOptions = () => ({
    validationSchema: Yup.object().shape({
      count_bedrooms: Yup.mixed().when('unit_type_scope_txt_id', {
        is: 'single',
        then: Yup.number().transform((value, originalValue) =>
          /\s/.test(originalValue) ? NaN : value
        ),
        otherwise: Yup.number()
          .transform((value, originalValue) => (/\s/.test(originalValue) ? NaN : value))
          .min(0)
          .max(99)
          .required()
      }),
      count_full_bathrooms: Yup.number()
        .transform((value, originalValue) => (/\s/.test(originalValue) ? NaN : value))
        .min(0)
        .max(99.5)
        .required(),
      count_dens: Yup.number()
        .transform((value, originalValue) => (/\s/.test(originalValue) ? NaN : value))
        .min(0)
        .max(99),
      max_occupancy: Yup.number().min(1),
      count_full_shared_bathrooms: Yup.mixed().when('unit_type_scope_txt_id', {
        is: 'entire',
        then: Yup.number().transform((value, originalValue) =>
          /\s/.test(originalValue) ? NaN : value
        ),
        otherwise: Yup.number()
          .transform((value, originalValue) => (/\s/.test(originalValue) ? NaN : value))
          .min(0)
          .max(99.5)
          .required()
      }),
      isGroupListing: Yup.bool(),
      size: Yup.mixed().when('building.state', {
        is: 'ON',
        then: Yup.string().notRequired(),
        otherwise: Yup.mixed().when('isGroupListing', {
          is: false,
          then: Yup.number()
            .transform((value, originalValue) => (/\s/.test(originalValue) ? NaN : value))
            .min(1)
            .required(),
          otherwise: Yup.string().notRequired()
        })
      }),
      gr_count: Yup.number(),
      gr_floors: Yup.string(),
      gr_min_size: Yup.mixed().when('isGroupListing', {
        is: true,
        then: Yup.number().min(1).required(),
        otherwise: Yup.string().notRequired()
      }),
      gr_max_size: Yup.mixed().when('isGroupListing', {
        is: true,
        then: Yup.number().min(1).required(),
        otherwise: Yup.string().notRequired()
      }),
      gr_min_price: Yup.mixed().when('isGroupListing', {
        is: true,
        then: Yup.number().positive().min(1).required(),
        otherwise: Yup.string().notRequired()
      }),
      gr_max_price: Yup.mixed().when('isGroupListing', {
        is: true,
        then: Yup.number().positive().min(1).required(),
        otherwise: Yup.string().notRequired()
      }),
      lease_type: Yup.string().required(),
      availability_date: Yup.date().required(),
      lease_length: Yup.string(),
      pet_limitations: Yup.bool(),
      allow_cats: Yup.number().min(0).max(99).nullable(),
      allow_dogs: Yup.number().min(0).max(99).nullable(),
      min_lease_period: Yup.mixed().when('lease_length', {
        is: 'other',
        then: Yup.number().positive().required(),
        otherwise: Yup.string()
      }),
      min_lease_period_type: Yup.string().when('lease_length', {
        is: 'other',
        then: Yup.string().required(),
        otherwise: Yup.string()
      }),
      price: Yup.mixed().when('isGroupListing', {
        is: false,
        then: Yup.number().positive().required(),
        otherwise: Yup.string().notRequired()
      }),
      price_frequency: Yup.string().required(),
      furnished: Yup.string(),
      parking: Yup.object().shape({
        enabled: Yup.bool(),
        hasFee: Yup.bool(),
        fee_frequency_txt_id: Yup.mixed().when('hasFee', {
          is: true,
          then: Yup.string()
            .test(
              'fee_frequency_txt_id',
              t('l.listing.edit.required.error', 'Required'),
              val => val && val !== 'free' && val !== 'not_available'
            )
            .required(),
          otherwise: Yup.string()
        }),
        fee: Yup.mixed().when('hasFee', {
          is: true,
          then: Yup.number()
            .moreThan(0, t('l.listing.edit.no_zero_fee.error', 'Fee must be greater than $0'))
            .required(),
          otherwise: Yup.string()
        })
      }),
      storage: Yup.object().shape({
        enabled: Yup.bool(),
        hasFee: Yup.bool(),
        fee_frequency_txt_id: Yup.mixed().when('hasFee', {
          is: true,
          then: Yup.string()
            .test(
              'fee_frequency_txt_id',
              t('l.listing.edit.required.error', 'Required'),
              val => val && val !== 'free' && val !== 'not_available'
            )
            .required(),
          otherwise: Yup.string()
        }),
        fee: Yup.mixed().when('hasFee', {
          is: true,
          then: Yup.number()
            .moreThan(0, t('l.listing.edit.no_zero_fee.error', 'Fee must be greater than $0'))
            .required(),
          otherwise: Yup.string()
        })
      }),
      description: Yup.string(),
      images: Yup.array().min(
        1,
        t('l.listing.edit.no_image_selected.error', 'Please upload at least 1 photo')
      ),
      isBoostYourListing: Yup.boolean(),
      duration: Yup.string().when('isBoostYourListing', {
        is: true,
        then: Yup.string().required('Duration is required when sponsored listing is checked'),
        otherwise: Yup.string().notRequired()
      })
    }),
    validate: values => {
      let errors = {}
      if (values.isGroupListing) {
        errors = {
          ...errors,
          ...validateMinMax(values, 'gr_min_size', 'gr_max_size'),
          ...validateMinMax(values, 'gr_min_price', 'gr_max_price')
        }
      }

      return errors
    },
    onSubmit: async (values, { setSubmitting }) => {
      const newValues = omit(['duration'], values)
      const hasVerifiedPhoneNumber =
        !isNullOrEmpty(session.phone_number) && session.phone_verified_state === 'passed'
      const action = hasVerifiedPhoneNumber ? saveAndPublish : handleVerifyPhoneModal

      await action(newValues, setSubmitting, !!isDraft(data))
    }
  })

  if (isEmpty(data)) return null

  const formattedValues = omit(['videos', 'state_machine'], data)
  const initialValues = { ...formattedValues, isBoostYourListing, duration: '' }

  const listingAddress = `${data?.unit_number ? `${data?.unit_number} - ` : ''}${
    data?.building?.full_street_name
  }`

  return (
    <Page>
      <Formik {...formOptions()} initialValues={initialValues}>
        {({ values, setFieldValue, isSubmitting, handleSubmit, errors }) => (
          <Form className="flex-full">
            <ContentWrapper className="mb-100">
              <Helmet>
                <title>{t('l.listing.create.create_listing.title', 'Create a New Listing')}</title>
              </Helmet>
              <PageTitle>{listingAddress}</PageTitle>
              {isDraft(data) && (
                <>
                  <AreaTitle>
                    {t('l.listing.edit.your_building.subtitle', 'Your Building')}
                  </AreaTitle>
                  <Panel vSpace={0} hSpace={0}>
                    <BuildingInfo
                      data={data.building}
                      link={{
                        to: `/listing/edit-building/${data.listing_id}`,
                        as: Link,
                        text: t('l.listing.edit.change_building.link', 'Change building')
                      }}
                    />
                  </Panel>
                </>
              )}

              <Details data={data} values={values} setFieldValue={setFieldValue} />

              <Terms data={data} values={values} />
              <Features values={values} />
              <ParkingStorage values={values} />
              <Description />
              <SponsoredListing
                setIsBoostYourListing={setIsBoostYourListing}
                setBoostYourListingData={setBoostYourListingData}
              />
              <AreaTitle>
                {t('l.listing.edit.upload_photos.subtitle', 'Upload Your Unit Photos')}
              </AreaTitle>
              <ImageUpload
                images={values.images}
                unitId={values.unit_id}
                listingId={values.listing_id}
                onChange={newImages => setFieldValue('images', newImages)}
              />
              <Error name="images" />
              <AreaTitle>{t('l.listing.edit.upload_video.subtitle', 'Upload Video')}</AreaTitle>
              <VideoUpload
                videoFiles={data.videos}
                unitId={values.unit_id}
                listingId={values.listing_id}
                uploadInProgress={uploading}
                videoLinks={data.listingLinks}
                onFileUploadStart={() => {
                  setUploading(true)
                  uploadToast.current = toast.progress({
                    title: t('l.listing.edit.uploading_video.text', 'Uploading Video')
                  })
                }}
                onFileUploadEnd={() => {
                  setUploading(false)
                  toast(videoSuccessMessage(), {
                    title: t(
                      'l.listing.edit.video_upload_completed.text',
                      'Video Upload Completed'
                    ),
                    type: 'success'
                  })
                }}
                onFileUploadError={() => {
                  setUploading(false)
                  toast.remove(uploadToast.current)
                  toast(videoFailMessage(), {
                    title: t('l.listing.edit.video_upload_fail.title', 'Video Upload Failed'),
                    type: 'error'
                  })
                }}
                onVideoLinkAddSuccess={() => {
                  toast(videoSuccessMessage(), {
                    title: t(
                      'l.listing.edit.video_link_upload_completed.text',
                      'Video Link Added Successfully'
                    ),
                    type: 'success'
                  })
                }}
                onFileUploadProgress={progress => {
                  toast.update(uploadToast.current, { progress })
                }}
              />
              <LandlordManagement
                primaryLandlord={primaryLandlord}
                setPrimaryLandlord={setPrimaryLandlord}
                secondaryLandlords={secondaryLandlords}
                setSecondaryLandlords={setSecondaryLandlords}
              />
            </ContentWrapper>
            <ListingButtonBar
              listingId={values.listing_id}
              saveAndExit={() => saveAndExit(values)}
              handlePreview={() => (isEmpty(errors) ? handlePreview(values) : handleSubmit())}
              isSubmitting={isSubmitting}
              isDraft={isDraft(data)}
            >
              <Inline>
                {!!isDraft(data) && (
                  <Hidden below="tablet">
                    <StyledButton
                      theme="text"
                      size="small"
                      disabled={isSubmitting}
                      loading={!primaryLandlord?.length}
                      isDraft={isDraft(data)}
                      onClick={() => (isEmpty(errors) ? handlePreview(values) : handleSubmit())}
                    >
                      {t('l.listing.edit.preview.button', 'Preview')}
                    </StyledButton>
                  </Hidden>
                )}
                <StyledButton
                  id="application-publish-listing-click"
                  className="listings-create-publish"
                  theme="primary"
                  size="small"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {getButtonText()}
                </StyledButton>
              </Inline>
            </ListingButtonBar>
            <Prompt
              when={uploading}
              message="You have unsaved changes, are you sure you want to leave?"
            />
          </Form>
        )}
      </Formik>
    </Page>
  )
}

export default Detail
